import React, {useState} from 'react';

import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {colors, transition} from '@app/styles/theme';

import circleIcon from '@pages/Relations/assets/circle.svg';
import noTaxIcon from '@pages/Relations/assets/no-tax.svg';
import openerIcon from '@pages/Relations/assets/opener.svg';
import squareIcon from '@pages/Relations/assets/square.svg';
import triangleIcon from '@pages/Relations/assets/triangle.svg';
import notFiledIcon from '@pages/Relations/assets/type/not-filed.svg';
import toDeductionIcon from '@pages/Relations/assets/type/to-deduction.svg';
import toPayIcon from '@pages/Relations/assets/type/to-pay.svg';
import zeroIcon from '@pages/Relations/assets/type/zero.svg';

const Wrapper = styled.div`
    padding: 16px;
`;

const Toggle = styled.button`
    border: 0;
    background: transparent;
    color: ${colors.brandDark};
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    padding: 0;
`;

const ToggleIcon = styled.img<{isOpen: boolean}>`
    margin-left: 8px;
    transition: transform ${transition.duration};
    transform: rotate(${({isOpen}) => (isOpen ? '0deg' : '180deg')});
`;

const Content = styled.div<{isOpen: boolean}>`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: ${({isOpen}) => (isOpen ? '1fr' : '0fr')};
    gap: 16px 50px;
    transition: all ${transition.duration};
    margin-top: ${({isOpen}) => (isOpen ? '10px' : '0')};

    & > * {
        overflow: hidden;
    }

    @media (max-width: 1100px) {
        grid-template-columns: 1fr;
        grid-template-rows: ${({isOpen}) => (isOpen ? '1fr 1fr' : '0fr 0fr')};
    }
`;

const Group = styled.div``;

const Title = styled.div`
    display: block;
    margin-top: 8px;
    margin-bottom: 8px;
    color: ${colors.textSecondary};
    font-size: 14px;
    font-weight: 600;
`;

const GroupItems = styled.div`
    max-width: 400px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 20px 20px;
    gap: 8px;
`;

const Item = styled.span`
    display: flex;
    align-items: center;
    color: ${colors.textPrimary};
    font-size: 12px;
    line-height: 16px;
`;

const ItemIcon = styled.img`
    width: 16px;
    height: 16px;
    margin-right: 8px;
    flex: 0 0 auto;
`;

const taxpayerItems = [
    {icon: squareIcon, label: 'page_relations.taxpayer_type.gap_source'},
    {icon: triangleIcon, label: 'page_relations.taxpayer_type.transit'},
    {icon: circleIcon, label: 'page_relations.taxpayer_type.other'},
    {icon: noTaxIcon, label: 'page_relations.taxpayer_type.no_tax'},
];

const declarationItems = [
    {icon: toDeductionIcon, label: 'page_relations.declaration_type.return'},
    {icon: toPayIcon, label: 'page_relations.declaration_type.pay'},
    {icon: zeroIcon, label: 'page_relations.declaration_type.zero'},
    {icon: notFiledIcon, label: 'page_relations.declaration_type.not_filled'},
];

export const RelationsLegend = () => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Wrapper>
            <Toggle type="button" onClick={() => setIsOpen((prev) => !prev)}>
                {isOpen ? t('page_relations.legend.hide') : t('page_relations.legend.show')}
                <ToggleIcon src={openerIcon} alt="Toggle" isOpen={isOpen} />
            </Toggle>

            <Content isOpen={isOpen}>
                <Group>
                    <Title>{t('page_relations.taxpayer_type.title')}</Title>
                    <GroupItems>
                        {taxpayerItems.map((item) => (
                            <Item key={item.label}>
                                <ItemIcon src={item.icon} alt={item.label} />
                                {t(item.label)}
                            </Item>
                        ))}
                    </GroupItems>
                </Group>

                <Group>
                    <Title>{t('page_relations.declaration_type.title')}</Title>
                    <GroupItems>
                        {declarationItems.map((item) => (
                            <Item key={item.label}>
                                <ItemIcon src={item.icon} alt={item.label} />
                                {t(item.label)}
                            </Item>
                        ))}
                    </GroupItems>
                </Group>
            </Content>
        </Wrapper>
    );
};
